import React, { useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

interface ImageItem {
  src: string;
  caption?: string;
}

interface CarouselProps {
  items: ImageItem[];
}

function Carousel({ items }: CarouselProps) {
  const images = items.map(item => item.src);
  const captions = items.map(item => item.caption || '');
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => {
      const maxIndex = Math.max(0, images.length - 4);
      return prevIndex === 0 ? maxIndex : prevIndex - 1;
    });
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => {
      const maxIndex = Math.max(0, images.length - 4);
      return prevIndex >= maxIndex ? 0 : prevIndex + 1;
    });
  };

  const goToSlide = (index: number) => {
    const maxIndex = Math.max(0, images.length - 4);
    setCurrentIndex(Math.min(index, maxIndex));
  };

  return (
    <div className="relative w-full px-16">
      <div className="relative overflow-hidden rounded-lg">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ 
            transform: `translateX(-${currentIndex * 25}%)` 
          }}
        >
          {images.map((image, index) => (
            <div key={index} className="min-w-[25%] px-2 relative group">
              <img 
                src={image} 
                alt={`Slide ${index + 1}`}
                className="w-full h-auto object-cover rounded-lg"
              />
              {captions[index] && (
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                  <p className="text-white text-sm px-4 text-center">
                    {captions[index]}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      {images.length > 4 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-3 rounded-full transition z-10 hover:opacity-80"
            aria-label="Previous images"
          >
            <FaChevronLeft size={20} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-3 rounded-full transition z-10 hover:opacity-80"
            aria-label="Next images"
          >
            <FaChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {images.length > 4 && (
        <div className="flex justify-center mt-4 space-x-2">
          {Array.from({ length: Math.max(1, images.length - 3) }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition ${
                index === currentIndex ? 'bg-white' : 'bg-gray-500'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Carousel;

